const PERF_SENTINEL_RING_LIMIT = 96;

export function performanceNowMs() {
  if (typeof globalThis.performance?.now === "function") {
    return globalThis.performance.now();
  }
  return Date.now();
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function searchExpressionStats(expression) {
  const visit = (node, depth) => {
    if (!isPlainObject(node)) {
      return { nodes: 0, terms: 0, maxDepth: 0 };
    }
    if (
      node.type === "group" &&
      node.negated !== true &&
      (!Array.isArray(node.children) || node.children.length === 0)
    ) {
      return { nodes: 0, terms: 0, maxDepth: 0 };
    }
    if (node.type === "term") {
      return { nodes: 1, terms: 1, maxDepth: depth };
    }
    const children = node.type === "container" && isPlainObject(node.expression)
      ? [node.expression]
      : Array.isArray(node.children) ? node.children : [];
    let nodes = 1;
    let terms = 0;
    let maxDepth = depth;
    for (const child of children) {
      const childStats = visit(child, depth + 1);
      nodes += childStats.nodes;
      terms += childStats.terms;
      maxDepth = Math.max(maxDepth, childStats.maxDepth);
    }
    return { nodes, terms, maxDepth };
  };
  return visit(expression, 1);
}

function countRecordListValues(value) {
  if (!isPlainObject(value)) {
    return { groups: 0, values: 0 };
  }
  let groups = 0;
  let values = 0;
  for (const entry of Object.values(value)) {
    if (!Array.isArray(entry) || entry.length === 0) {
      continue;
    }
    groups += 1;
    values += entry.length;
  }
  return { groups, values };
}

function summarizeFilterPerfMetrics(filters) {
  const disabledBindings = countRecordListValues(filters?.layerFilterBindingIdsDisabledByLayer);
  const searchStats = searchExpressionStats(filters?.searchExpression);
  return {
    search_nodes: searchStats.nodes,
    search_terms: searchStats.terms,
    search_max_depth: searchStats.maxDepth,
    visible_layers: Array.isArray(filters?.layerIdsVisible) ? filters.layerIdsVisible.length : 0,
    ordered_layers: Array.isArray(filters?.layerIdsOrdered) ? filters.layerIdsOrdered.length : 0,
    disabled_binding_layers: disabledBindings.groups,
    disabled_binding_ids: disabledBindings.values,
    clip_mask_layers: isPlainObject(filters?.layerClipMasks)
      ? Object.keys(filters.layerClipMasks).length
      : 0,
  };
}

export function recordFilterPerfMetrics(collector, prefix, filters) {
  for (const [name, value] of Object.entries(summarizeFilterPerfMetrics(filters))) {
    setPerformanceLast(collector, `${prefix}.${name}`, value);
  }
}

export function createPerfSentinel(enabled = false) {
  return {
    enabled: enabled === true,
    installed: false,
    startedAtMs: performanceNowMs(),
    loafSupported: false,
    loafObserver: null,
    listeners: [],
    loaf: [],
    bridgeIngress: [],
    bridgeEgress: [],
    listenerTimings: [],
    shellDrains: [],
    hotPayloadViolations: [],
    networkRequests: [],
    networkCounts: {},
    cacheOperations: [],
    cacheCounts: {},
    runtimeManifest: null,
    threadStatus: null,
    recentErrors: [],
  };
}

export function pushRing(buffer, entry, limit = PERF_SENTINEL_RING_LIMIT) {
  if (!Array.isArray(buffer)) {
    return;
  }
  buffer.push(entry);
  while (buffer.length > limit) {
    buffer.shift();
  }
}

export function normalizeErrorRecord(error, context = {}) {
  const message =
    error && typeof error === "object" && "message" in error
      ? String(error.message)
      : String(error ?? "");
  const stack =
    error && typeof error === "object" && "stack" in error
      ? String(error.stack || "")
      : "";
  return {
    atMs: performanceNowMs(),
    source: String(context.source || "unknown"),
    message: message || "unknown error",
    ...(stack ? { stack: stack.slice(0, 4000) } : {}),
    ...(context.type ? { type: String(context.type) } : {}),
  };
}

export function normalizeLoafEntry(entry) {
  const scripts = Array.isArray(entry?.scripts)
    ? entry.scripts.slice(0, 8).map((script) => ({
        name: String(script?.name || ""),
        sourceURL: String(script?.sourceURL || script?.sourceUrl || ""),
        duration: Number(script?.duration || 0),
        executionStart: Number(script?.executionStart || 0),
      }))
    : [];
  return {
    startTime: Number(entry?.startTime || 0),
    duration: Number(entry?.duration || 0),
    blockingDuration: Number(entry?.blockingDuration || 0),
    renderStart: Number(entry?.renderStart || 0),
    styleAndLayoutStart: Number(entry?.styleAndLayoutStart || 0),
    scripts,
  };
}

export function createPerformanceCollector(scenario = "browser") {
  return {
    scenario: String(scenario || "browser"),
    startedAtMs: performanceNowMs(),
    spanSamples: new Map(),
    counters: new Map(),
    gaugeTotals: new Map(),
    lastValues: new Map(),
  };
}

export function normalizePerformanceOptions(options = {}) {
  const warmupFrames = Number.parseInt(options.warmupFrames, 10);
  const captureTrace = options.captureTrace === true;
  return {
    scenario: String(options.scenario || "browser").trim() || "browser",
    warmupFrames: Number.isFinite(warmupFrames) && warmupFrames >= 0 ? warmupFrames : 0,
    captureTrace,
    captureSpans: captureTrace || options.captureSpans === true,
    captureFrames: options.captureFrames === true,
  };
}

export function addPerformanceSpanSample(collector, name, durationMs) {
  if (!collector || !name) {
    return;
  }
  const samples = collector.spanSamples.get(name) || [];
  samples.push(Math.max(0, Number(durationMs) || 0));
  collector.spanSamples.set(name, samples);
}

export function addPerformanceCounter(collector, name, delta = 1) {
  if (!collector || !name) {
    return;
  }
  const nextValue = (collector.counters.get(name) || 0) + Number(delta || 0);
  collector.counters.set(name, nextValue);
}

export function addPerformanceGauge(collector, name, value) {
  if (!collector || !name) {
    return;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return;
  }
  const current = collector.gaugeTotals.get(name) || { sum: 0, count: 0 };
  collector.gaugeTotals.set(name, {
    sum: current.sum + numericValue,
    count: current.count + 1,
  });
}

export function setPerformanceLast(collector, name, value) {
  if (!collector || !name) {
    return;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return;
  }
  collector.lastValues.set(name, numericValue);
}

function summarizeSamples(samples) {
  if (!Array.isArray(samples) || samples.length === 0) {
    return {
      count: 0,
      avg_ms: 0,
      p50_ms: 0,
      p95_ms: 0,
      p99_ms: 0,
      max_ms: 0,
      total_ms: 0,
    };
  }
  const sorted = samples.slice().sort((left, right) => left - right);
  const totalMs = sorted.reduce((sum, value) => sum + value, 0);
  const pick = (p) => sorted[Math.min(sorted.length - 1, Math.round((sorted.length - 1) * p))];
  return {
    count: sorted.length,
    avg_ms: totalMs / sorted.length,
    p50_ms: pick(0.5),
    p95_ms: pick(0.95),
    p99_ms: pick(0.99),
    max_ms: sorted[sorted.length - 1],
    total_ms: totalMs,
  };
}

export function snapshotPerformanceCollector(collector) {
  const namedSpans = Object.fromEntries(
    Array.from(collector?.spanSamples || [], ([name, samples]) => [name, summarizeSamples(samples)]),
  );
  const counters = Object.fromEntries(collector?.counters || []);
  Object.assign(counters, Object.fromEntries(collector?.lastValues || []));
  for (const [name, value] of collector?.gaugeTotals || []) {
    counters[`${name}_avg`] = value.count > 0 ? value.sum / value.count : 0;
  }
  return {
    scenario: collector?.scenario || "browser",
    elapsed_ms: Math.max(0, performanceNowMs() - (collector?.startedAtMs || performanceNowMs())),
    named_spans: namedSpans,
    counters,
  };
}

export function emptyQuantileSummary() {
  return {
    avg: 0,
    p50: 0,
    p95: 0,
    p99: 0,
    max: 0,
  };
}

export function emptyProfileSummary(scenario = "browser", warmupFrames = 0) {
  return {
    scenario,
    bevy_version: null,
    git_revision: null,
    build_profile: "browser",
    frames: 0,
    warmup_frames: warmupFrames,
    wall_clock_ms: 0,
    frame_time_ms: emptyQuantileSummary(),
    named_spans: {},
    counters: {},
  };
}

export function normalizePerfCounterSuffix(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}
